import {Component, OnInit, signal} from '@angular/core';
import {ReactiveFormsModule} from "@angular/forms";
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { CommonModule } from '@angular/common';
import { lastValueFrom } from 'rxjs'; 
import { MatInputModule } from '@angular/material/input';

import { EmailInputComponent } from '../email-input/email-input.component';
import { OtpInputComponent } from '../otp-input/otp-input.component';
import { Router } from '@angular/router';
import { UsersService } from '../../../shared/services/users/users.service';
import { ProviderRegistration } from '../../models/provider-registration.model';
import { ErrorHandlingService, UserFriendlyError } from '../../../shared/services/error-handling.service';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    imports: [
    CommonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatGridListModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    ReactiveFormsModule,
    EmailInputComponent,
    OtpInputComponent
]
})
export class LoginComponent implements OnInit{
  otpSent: boolean = false;
  userEmail: string = '';
  sessionInfo: string | null = null;
  isLoading: boolean = false;
  errorMessage: string | null = null;
  currentError: UserFriendlyError | null = null;
  loadingMessage: string = '';
  shouldShowRetry: boolean = false;
  private lastAction: 'email' | 'otp' | null = null;
  private lastActionData: any = null;

  isBetaEnvironment = signal<boolean>(false);
  isOldDomain = signal<boolean>(false);

  constructor(
    private usersService: UsersService,
    private router: Router,
    private errorHandlingService: ErrorHandlingService
  ) {}

  ngOnInit(): void {
    // Determine if the app is running in a beta environment
    const hostname = window.location.hostname;
    this.isBetaEnvironment.set(hostname.includes('beta'));
    this.isOldDomain.set(hostname.includes('sillion'));
  }


  async handleEmailSubmit(email: string): Promise<void> {
    this.userEmail = email;
    this.lastAction = 'email';
    this.lastActionData = email;
    this.isLoading = true;
    this.loadingMessage = 'Enviando código de verificación...';
    this.clearError();

    try {
      const response = await lastValueFrom(this.usersService.login(this.userEmail));
      this.sessionInfo = response?.session || null;

      if (this.sessionInfo) {
        this.otpSent = true;
        // Show success message briefly
        this.showSuccessMessage('Código enviado exitosamente a su email');
      } else {
        throw new Error('No session info received');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      this.handleError(error);
      this.otpSent = false;
    } finally {
      this.isLoading = false;
      this.loadingMessage = '';
    }
  }


  async handleOtpSubmit(otp: string): Promise<any> {
    if (!this.sessionInfo) {
      console.error('No session info available');
      this.showError({
        title: 'Error de sesión',
        message: 'No hay información de sesión disponible. Por favor, solicite un nuevo código.',
        type: 'error'
      });
      return;
    }

    this.lastAction = 'otp';
    this.lastActionData = otp;
    this.isLoading = true;
    this.loadingMessage = 'Verificando código...';
    this.clearError();

    try {
      const result = await lastValueFrom(
        this.usersService.verifyOtp(this.userEmail, otp, this.sessionInfo)
      );
      this.router.navigate(['/dashboard']);
      return result;
    } catch (error) {
      console.error('Error verifying OTP:', error);
      this.handleError(error);
      throw error;
    } finally {
      this.isLoading = false;
      this.loadingMessage = '';
    }
  }

  handleBackToEmail(): void {
    this.otpSent = false;
    this.userEmail = '';
    this.sessionInfo = null;
    this.clearError();
  }

  handleProviderRegistration(providerData: ProviderRegistration): void {
    // Here you would typically send the provider registration data to your backend
    console.log('Provider registration data received:', providerData);
    
    // Show a more user-friendly success message
    this.showSuccessMessage(
      `Solicitud de registro enviada exitosamente para ${providerData.commercialName}. ` +
      'Recibirá una respuesta por email en las próximas 24-48 horas.'
    );
    
    // TODO: Implement actual API call to register the provider
    // Example:
    // try {
    //   await this.providerService.registerProvider(providerData);
    //   this.showSuccessMessage('Registro enviado exitosamente');
    // } catch (error) {
    //   this.handleError(error);
    // }
  }

  // Error handling methods
  private handleError(error: any): void {
    const userError = this.errorHandlingService.handleError(error);
    this.showError(userError);
    this.shouldShowRetry = this.errorHandlingService.shouldRetry(error);
  }

  private showError(error: UserFriendlyError): void {
    this.currentError = error;
    this.errorMessage = error.message;
  }

  private showSuccessMessage(message: string): void {
    this.currentError = {
      title: 'Éxito',
      message: message,
      type: 'info'
    };
    this.errorMessage = message;

    // Auto-hide success message after 5 seconds
    setTimeout(() => {
      if (this.currentError?.type === 'info') {
        this.clearError();
      }
    }, 5000);
  }

  clearError(): void {
    this.errorMessage = null;
    this.currentError = null;
    this.shouldShowRetry = false;
  }

  retryLastAction(): void {
    if (!this.lastAction || this.isLoading) {
      return;
    }

    this.clearError();

    switch (this.lastAction) {
      case 'email':
        if (this.lastActionData) {
          this.handleEmailSubmit(this.lastActionData);
        }
        break;
      case 'otp':
        if (this.lastActionData) {
          this.handleOtpSubmit(this.lastActionData);
        }
        break;
    }
  }

  formatErrorMessage(message: string): string {
    // Convert line breaks to HTML breaks for better display
    return message.replace(/\n/g, '<br>');
  }
}


